import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import InfoSvg from "@redotech/redo-icons/info-circle.svg";
import { ClientUploader } from "@redotech/redo-merchant-app-common/client/upload";
import type { AutoReplyAction as AutoReplyActionType } from "@redotech/redo-model/advanced-flow/rules/schemas";
import { Attachment } from "@redotech/redo-model/create-conversation-body";
import { MacroVariable as MacroVariableType } from "@redotech/redo-model/macro";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import {
  insertMacroVariableIntoQuill,
  MacroVariableSidebar,
} from "@redotech/redo-web/macro-variables";
import { Switch } from "@redotech/redo-web/switch";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import {
  SIGNATURE_MARKER,
  stripSignature,
} from "@redotech/util/email-signature";
import Quill from "quill";
import { memo, useEffect, useRef, useState } from "react";
import { StepRichTextEditor } from "../../../../../chat-flow/step-rich-text";

export const AutoReplyAction = memo(function AutoReplyAction({
  action,
  onChange,
  editableFields,
}: {
  action: AutoReplyActionType;
  onChange: (updated: AutoReplyActionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  // Any of the auto-reply fields being bound unlocks the whole editor;
  // none bound locks it.
  const isLocked =
    editableFields !== undefined &&
    !["textContent", "htmlContent", "attachments"].some((f) =>
      editableFields.has(f),
    );

  const team = useRequiredContext(TeamContext);
  const [showPlaintext, setShowPlaintext] = useState(false);
  const quillRef = useRef<Quill | undefined>(undefined);

  const handleMessage = useHandler(() => {
    if (isLocked) {
      return;
    }
    if (!quillRef.current) {
      return;
    }
    let textContent = quillRef.current.getText();
    let htmlContent: string | undefined = quillRef.current.getSemanticHTML();
    if (textContent === "\n") {
      textContent = "";
    }
    if (htmlContent === "<p></p>") {
      htmlContent = "";
    }

    if (
      neuterImgTags(htmlContent) !== neuterImgTags(action.htmlContent) ||
      textContent !== action.textContent
    ) {
      onChange({ ...action, textContent, htmlContent });
    }
  });

  const handleAttachmentUpload = (attachment: Attachment) => {
    if (isLocked) {
      return;
    }
    onChange({
      ...action,
      attachments: [...(action.attachments || []), attachment],
    });
  };

  const handleAttachmentRemoval = (url: string) => {
    if (isLocked) {
      return;
    }
    onChange({
      ...action,
      attachments: (action.attachments || []).filter(
        (attachment: Attachment) => attachment.url !== url,
      ),
    });
  };

  useEffect(() => {
    if (quillRef.current && team?.settings?.support?.emailSignature) {
      const signature = `<br><br><p>&#x200B;--</p>${team?.settings?.support?.emailSignature}`;
      const quill: Quill = quillRef.current;
      const signatureIndex = quill.getText().indexOf(SIGNATURE_MARKER);
      if (signatureIndex === -1) {
        quill.clipboard.dangerouslyPasteHTML(quill.getLength() - 1, signature);
      }
    }
  }, [team]);

  const handleVariableClick = (variable: MacroVariableType) => {
    if (isLocked) {
      return;
    }
    if (!quillRef.current) {
      return;
    }
    insertMacroVariableIntoQuill(quillRef.current, variable);
    handleMessage();
  };

  return (
    <ClientUploader>
      <Flex
        dir="column"
        gap="sm"
        style={isLocked ? { pointerEvents: "none", opacity: 0.6 } : undefined}
        w="full"
      >
        <Flex align="center" justify="space-between" w="full">
          <Text>Message to customer</Text>
          <Flex align="center" gap="xs">
            <Tooltip
              placement="bottom-start"
              title="The formatted version of the message will be used when replying to email messages. When responding to other message types such as Facebook or SMS, the plaintext message will be sent."
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <InfoSvg />
              </div>
            </Tooltip>
            <Text fontSize="xs" style={{ color: "#666" }}>
              Show plaintext
            </Text>
            <Switch onChange={setShowPlaintext} value={showPlaintext} />
          </Flex>
        </Flex>
        {showPlaintext && (
          <Text
            fontSize="sm"
            style={{
              whiteSpace: "pre-wrap",
              padding: "12px",
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
            }}
          >
            {stripSignature(action.textContent)}
          </Text>
        )}
        {!showPlaintext && (
          <Flex dir="column" gap="md" w="full">
            <Flex>
              <StepRichTextEditor
                attachments={action.attachments}
                enableAttachments
                htmlValue={action.htmlContent}
                onAttachmentRemoval={handleAttachmentRemoval}
                onAttachmentUpload={handleAttachmentUpload}
                onTextChange={handleMessage}
                quillRef={quillRef}
                textValue={action.textContent}
              />
            </Flex>
            <Flex overflowY="auto" style={{ maxHeight: "300px" }} w="full">
              <MacroVariableSidebar onVariableClick={handleVariableClick} />
            </Flex>
          </Flex>
        )}
      </Flex>
    </ClientUploader>
  );
});

const neuterImgTags = (htmlContent: string) => {
  const imgRegex = /<img.*?>/g;
  return htmlContent.replace(imgRegex, "<img />");
};
